import React, { useState } from 'react';
import styled from 'styled-components';
import { useSwipeable } from 'react-swipeable';
import { motion, AnimatePresence } from 'framer-motion';
import { threePracticaLocales } from '../../locales/firstScreen';
import { Footer } from '../UI/Footer/Footer';

const LANG_KEY = 'stonpools-lang';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled(motion.div)`
  background: #151515;
  width: 100%;
  max-width: 380px;
  border-radius: 20px;
  padding: 30px 10px;
  margin: 10px;
  position: relative;
`;

const ModalText = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  color: #ffffff;
  text-align: center;
  line-height: 140%;
  margin: 0 0 16px 0;
`;

const ModalButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 50px;
`;

const ModalButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  align-self: stretch;
  background: transparent;
  padding: 10px;
  margin-bottom: 10px;
  border-radius: 12px;
  border: 2px solid rgba(180, 180, 180, 0.2);

  &:last-child {
    margin-bottom: 0;
  }
`;

const ModalButtonImage = styled.img`
  width: 46px;
  height: 26px;
  margin-right: 10px;
  border-radius: 12px;
  object-fit: fill;
`;

const ModalButtonText = styled.span`
  color: #ffffff;
  font-size: 13px;
  line-height: 76%;
  letter-spacing: -2%;
  font-weight: 600;
  text-transform: uppercase;
`;

const modalVariants = {
  hidden: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.2,
    },
  },
  exit: {
    opacity: 0,
  },
};

const modalContentVariants = {
  hidden: {
    scale: 0.8,
    opacity: 0,
  },
  visible: {
    scale: 1,
    opacity: 1,
    transition: {
      duration: 0.3,
      ease: 'easeOut',
    },
  },
  exit: {
    scale: 0.8,
    opacity: 0,
    transition: {
      duration: 0.3,
      ease: 'easeIn',
    },
  },
};

const Container = styled.div`
  background: #151515;
  width: 100%;
  height: 100vh;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center; // Добавляем выравнивание по центру
  overflow-y: auto; // Добавляем скролл
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
  padding: 0 10px;
  padding-bottom: 100px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  margin: 50px 0 30px;
  padding: min(13px, 2vh);
  border-radius: 20px;
  justify-content: center;
  width: 100%;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: url(/assets/background.gif) center/cover no-repeat;
    mix-blend-mode: normal;
    opacity: 0.9;
    border-radius: 20px;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: #7354f2;
    opacity: 1;
    mix-blend-mode: multiply;
    border-radius: 20px;
  }

  /* Обеспечиваем видимость контента поверх оверлеев */
  > * {
    position: relative;
    z-index: 2;
  }
`;

const QuestionBlock = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  position: relative;
`;

const QuestionText = styled.span`
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-size: clamp(26px, 5vw, 26px);
  font-weight: 600;
  line-height: 90%;
  letter-spacing: -0.02em;
  text-align: center;
  white-space: pre-line;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Title = styled.h2`
  color: #ffffff;
  font-family: 'Manrope', sans-serif;
  font-size: 42px;
  margin-bottom: 15px;
  margin-top: min(1px, 0.2vh);
  line-height: 80%;
  font-weight: 600;
  text-align: center;
`;

const PracticaButton = styled.button`
  background: transparent;
  border: 2px solid #ffffff;
  border-radius: 8px;
  padding: 6px;
  color: #ffffff;
  font-size: 13px;
  font-weight: bold;
`;

const Description = styled.p`
  color: #b4b4b4;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  text-align: center;
  margin: 20px 0;
  line-height: 145%;
  letter-spacing: -0.01em;
  white-space: pre-line; /* добавляем поддержку переноса строк */
`;

const TableContainer = styled.div`
  background: #ffffff0d;
  padding-top: 16px;
  width: 100%;
`;

const TableHeader = styled.div`
  display: flex;
  align-items: start;
  margin: 0 10px 16px;
  color: #b4b4b4;
  font-size: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid #ffffff1a;
`;

const PoolRow = styled.div`
  display: flex;
  align-items: start;
  padding: 16px 10px;
  border-bottom: 1px solid #ffffff1a;

  &:last-child {
    border-bottom: none;
  }
`;

const PoolInfo = styled.div`
  display: flex;
  flex-direction: column;
  width: 35%; // Фиксированная ширина для колонки с названием
  margin-right: 12px;
`;

const StatContainer = styled.div`
  display: flex;
  flex: 1;
  justify-content: space-between;
  align-items: flex-start;
`;

const PoolImage = styled.img`
  width: 46px;
  height: 26px;
  margin-bottom: 4px;
  object-fit: contain;
`;

const PoolName = styled.span`
  color: #ffffff;
  font-size: 12px;
`;

const PoolStat = styled.span`
  color: #ffffff;
  font-size: 12px;
  margin-top: 30px;
  text-align: right;
  min-width: 60px; // Минимальная ширина для цифр
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-self: stretch;
  margin: 20px 10px; // Увеличиваем отступ сверху
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  justify-content: center; // Добавляем центрирование содержимого
  text-align: center; // Центрируем текст
  padding: 18px 21px;
  margin-bottom: 10px;
  border-radius: 12px;
  border: 2px solid rgba(180, 180, 180, 0.2);
  background: none;
  color: #ffffff;
  font-family: 'Manrope', sans-serif;
  font-size: 13px;
  font-weight: 600;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ModalTitle = styled.h3`
  font-family: 'Manrope', sans-serif;
  font-size: 26px;
  font-weight: 600;
  color: #ffffff;
  margin: 10px 0 20px 0;
  text-align: center;
  white-space: pre-line;
  line-height: 90%;
  letter-spacing: -0.02em;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 22px;
  right: 22px;
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  width: 12px;
  height: 12px;

  &::before,
  &::after {
    content: '';
    position: absolute;
    width: 15px;
    height: 2px;
    background: #ffffff;
    top: 50%;
    left: 50%;
  }

  &::before {
    transform: translate(-50%, -50%) rotate(45deg);
  }

  &::after {
    transform: translate(-50%, -50%) rotate(-45deg);
  }
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 18px 15px;
  margin-top: 20px;
  border-radius: 12px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  background: none;
  color: #ffffff;
  font-family: 'Manrope', sans-serif;
  font-size: 13px;
  font-weight: 600;
  width: 100%;
  text-transform: uppercase;
`;

const HomeButtonIcon = styled.img`
  width: 22px;
  height: 22px;
  object-fit: contain;
`;

const HomeButton = styled.button`
  position: absolute;
  top: 0;
  left: 0;
  margin: 0;
  background-color: transparent;
`;

type PoolName =
  | 'Token A / Token B'
  | 'Token C / Token D'
  | 'Token E / Token F'
  | 'Token G / Token H';

type PoolFeedback = {
  title: string;
  description: string;
  isCorrect: boolean;
};

const poolIcons: Record<PoolName, string> = {
  'Token A / Token B': '/assets/tokenAB.svg',
  'Token C / Token D': '/assets/tokenCD.svg',
  'Token E / Token F': '/assets/tokenEF.svg',
  'Token G / Token H': '/assets/tokenGH.svg',
};

const ThreePractica1: React.FC<{
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onNavigateToTheory?: () => void;
  onNavigateToPractica2?: () => void;
  toHome?: () => void;
}> = ({
  onSwipeLeft,
  onSwipeRight,
  onNavigateToTheory,
  onNavigateToPractica2,
  toHome,
}) => {
  const [selectedPool, setSelectedPool] = useState<PoolFeedback | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPoolSelectModalOpen, setIsPoolSelectModalOpen] = useState(false);

  const lang = (localStorage.getItem(LANG_KEY) as 'ru' | 'en') || 'ru';
  const t = threePracticaLocales[lang][1];

  // Типизируем pools и poolFeedback
  const pools = t.pools as {
    name: PoolName;
    apr: string;
    tvl: string;
    volume: string;
  }[];
  const poolFeedback = t.modal.feedback as Record<PoolName, PoolFeedback>;

  const handlePoolClick = (poolName: PoolName) => {
    setSelectedPool(poolFeedback[poolName]);
    setIsModalOpen(true);
  };

  const handlers = useSwipeable({
    onSwipedLeft: () => onSwipeLeft?.(),
    onSwipedRight: () => onSwipeRight?.(),
    trackMouse: true,
    delta: 50,
    swipeDuration: 500,
  });

  return (
    <div {...handlers}>
      <Container>
        <HomeButton onClick={toHome}>
          <HomeButtonIcon src="/assets/homeIconPurple.svg" />
        </HomeButton>
        <Header>
          <Title>{t.title}</Title>
          <PracticaButton>{t.practica}</PracticaButton>
        </Header>
        <QuestionBlock>
          <QuestionText>{t.question}</QuestionText>
        </QuestionBlock>
        <Description>{t.description}</Description>
        <TableContainer>
          <TableHeader>
            <span style={{ width: '35%' }}>{t.table.poolName}</span>
            <StatContainer>
              <span style={{ minWidth: '60px', textAlign: 'right' }}>
                {t.table.apr}
              </span>
              <span style={{ minWidth: '80px', textAlign: 'right' }}>
                {t.table.tvl}
              </span>
              <span style={{ minWidth: '70px', textAlign: 'right' }}>
                {t.table.volume}
              </span>
            </StatContainer>
          </TableHeader>

          {pools.map((pool, index) => (
            <PoolRow
              key={index}
              onClick={() => handlePoolClick(pool.name)}
              style={{ cursor: 'pointer' }}
            >
              <PoolInfo>
                <PoolImage src={poolIcons[pool.name]} alt={pool.name} />
                <PoolName>{pool.name}</PoolName>
              </PoolInfo>
              <StatContainer>
                <PoolStat>{pool.apr}</PoolStat>
                <PoolStat>{pool.tvl}</PoolStat>
                <PoolStat>{pool.volume}</PoolStat>
              </StatContainer>
            </PoolRow>
          ))}
        </TableContainer>

        <AnimatePresence>
          {isPoolSelectModalOpen && (
            <ModalOverlay
              initial="hidden"
              animate="visible"
              exit="exit"
              variants={modalVariants}
              onClick={() => setIsPoolSelectModalOpen(false)}
            >
              <ModalContent
                onClick={(e) => e.stopPropagation()}
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={modalContentVariants}
              >
                <ModalText>{t.modal.selectPool}</ModalText>
                <ModalButtonContainer>
                  {pools.map((pool, index) => (
                    <ModalButton
                      key={index}
                      onClick={() => {
                        handlePoolClick(pool.name);
                        setIsPoolSelectModalOpen(false);
                      }}
                    >
                      <ModalButtonImage
                        src={poolIcons[pool.name]}
                        alt={pool.name}
                      />
                      <ModalButtonText>{pool.name}</ModalButtonText>
                    </ModalButton>
                  ))}
                </ModalButtonContainer>
              </ModalContent>
            </ModalOverlay>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {isModalOpen && selectedPool && (
            <ModalOverlay
              initial="hidden"
              animate="visible"
              exit="exit"
              variants={modalVariants}
              onClick={() => setIsModalOpen(false)}
            >
              <ModalContent
                onClick={(e) => e.stopPropagation()}
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={modalContentVariants}
                style={{
                  background: selectedPool.isCorrect ? '#7354F2' : '#232323',
                  minHeight: '210px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <CloseButton onClick={() => setIsModalOpen(false)} />
                <ModalTitle>{selectedPool.title}</ModalTitle>
                <ModalText>{selectedPool.description}</ModalText>
                {selectedPool.isCorrect ? (
                  <ActionButton
                    onClick={() => {
                      setIsModalOpen(false);
                      setTimeout(() => {
                        onNavigateToPractica2?.();
                      }, 200);
                    }}
                  >
                    {t.modal.actionCorrect}
                  </ActionButton>
                ) : (
                  <ActionButton
                    onClick={() => {
                      setIsModalOpen(false);
                      setTimeout(() => {
                        onNavigateToTheory?.();
                      }, 200);
                    }}
                  >
                    {t.modal.actionWrong}
                  </ActionButton>
                )}
              </ModalContent>
            </ModalOverlay>
          )}
        </AnimatePresence>
        <ButtonContainer>
          <Button onClick={() => setIsPoolSelectModalOpen(true)}>
            {t.modal.showPools}
          </Button>
        </ButtonContainer>
        <Footer
          practica
          textLeft={t.back}
          onSwipeLeft={onSwipeLeft}
          onSwipeRight={onSwipeRight}
        />
      </Container>
    </div>
  );
};

export default ThreePractica1;
